import { useQuery } from "@tanstack/react-query";

import { api, unwrap, type Schemas } from "@/shared/api/client";

export type Feature = Schemas["Feature"];

/** The product areas switched on in this deployment (GET /features). */
export const featuresKey = ["features"] as const;

const NO_FEATURES: ReadonlySet<Feature> = new Set();

function fetchFeatures(): Promise<ReadonlySet<Feature>> {
  return unwrap(api.GET("/api/v1/features")).then((body) => new Set(body.features));
}

/**
 * The areas that are on, and whether the list has arrived. They change only with a deploy,
 * so the list is read once per page load.
 */
export function useFeatures(): { features: ReadonlySet<Feature>; isLoaded: boolean } {
  const query = useQuery({
    queryKey: featuresKey,
    queryFn: fetchFeatures,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return { features: query.data ?? NO_FEATURES, isLoaded: query.isSuccess };
}

/**
 * Whether an area is on. No feature means always on. Until the list arrives everything gated
 * reads as off, so a switched-off area never flashes into the navigation.
 */
export function useFeature(feature?: Feature): boolean {
  const { features } = useFeatures();
  return feature === undefined || features.has(feature);
}
